import * as React from 'react';
import {
  withStyles,
  WithStyles,
  createStyles,
  Theme,
} from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';

import Badge from 'ha/ui/next/Badge';
import { withIntl, IntlProps } from 'ha/i18n';
import formatPrice from 'ha/utils/formatPrice';
import units from 'ha/theme/units';
import { ListingPreviewCard } from 'ha/services/apiV2/types/AlgoliaListing';

const findCheapestRoom = (rooms: ListingPreviewCard[]) =>
  rooms.reduce(
    (expected, room) => (room.price < expected.price ? room : expected),
    rooms[0],
  );

interface Props {
  marker: {
    rooms: ListingPreviewCard[];
  };
  markerSize: {
    height: number;
    pointX: number;
    pointY: number;
  };
  emphasis: boolean;
  highlightedRoomId: number;
}

const styles = (t: Theme) =>
  createStyles({
    marker: {
      whiteSpace: 'nowrap',
      cursor: 'pointer',
      display: 'flex',
      '&:before': {
        position: 'absolute',
        content: '""',
        border: '6px solid',
        borderColor: `${t.palette.mint.main} transparent transparent transparent`,
        left: '50%',
        bottom: -11,
        marginLeft: '-6px',
      },
    },
    content: {
      backgroundColor: t.palette.mint.main,
      color: 'white',
      borderRadius: units.rem(0.2),
      fontSize: units.rem(1.5),
      padding: units.yx(0.3, 0.6),
      overflow: 'visible',
      display: 'flex',
      alignItems: 'center',
    },
  });

export const Pin: React.SFC<Props & IntlProps & WithStyles<typeof styles>> = ({
  classes,
  marker,
  markerSize: { pointX, pointY, height },
  emphasis,
  highlightedRoomId,
  intl: { T },
}) => {
  const scale = emphasis ? 1 : 0.5;
  const style: React.CSSProperties = {
    position: 'absolute',
    left: 0,
    top: 0,
    height,
    transform: `scale(${scale}, ${scale}) translateX(-50%)`,
    transformOrigin: `${pointX}px ${pointY}px`,
    transition:
      '-webkit-transform 0.25s cubic-bezier(0.485, 1.65, 0.545, 0.835)',
    cursor: 'pointer',
    zIndex: emphasis ? 1999 : 1998,
  };

  const highlightedRoom = marker.rooms.find(
    room => room.id === highlightedRoomId,
  );
  const cheapestRoom = findCheapestRoom(marker.rooms);
  const { price, currency } = highlightedRoom || cheapestRoom;
  const formattedPrice = formatPrice(price, currency);

  return (
    <div
      style={style}
      className={classes.marker}
      role="button"
      data-test-locator="Search Map Pin"
    >
      {marker.rooms.length > 1 ? (
        <Badge badgeContent={marker.rooms.length} color="secondary">
          <Typography variant="button" className={classes.content}>
            {T('From')} {formattedPrice}
          </Typography>
        </Badge>
      ) : (
        <Typography variant="button" className={classes.content}>
          {formattedPrice}
        </Typography>
      )}
    </div>
  );
};

export default withIntl(withStyles(styles)(Pin));
